import { Check } from 'lucide-react';

import type {
  DescriptorCompetition,
  DescriptorPlacement,
  ProductSearchImpact,
} from '~/features/catalog-operations/types/search-impact.types';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';

import { descriptorCoverage, formatVolume } from './search-impact-utils';

interface Props {
  data: ProductSearchImpact;
}

const COMPETITION_BEAD: Record<DescriptorCompetition, string> = {
  Low: 'bg-success',
  Medium: 'bg-caution',
  High: 'bg-error',
};

/** One placement cell: tick when placed, dash when not, faint dot when the field can't carry the term. */
function PlacementCell({ placement }: { placement: DescriptorPlacement }) {
  const label =
    placement === 'placed'
      ? 'Placed'
      : placement === 'not-placed'
        ? 'Not placed'
        : 'Not applicable';
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        'mx-auto inline-grid h-6.5 w-6.5 place-items-center rounded-lg text-xs font-semibold',
        placement === 'placed' && 'bg-success-50 text-success',
        placement === 'not-placed' && 'bg-muted text-muted-foreground',
        placement === 'not-applicable' && 'text-muted-foreground/40',
      )}
    >
      {placement === 'placed' ? (
        <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
      ) : placement === 'not-placed' ? (
        '–'
      ) : (
        '·'
      )}
    </span>
  );
}

/** Descriptor × field matrix: which product fields each search descriptor actually reached. */
export function DescriptorCoverageMatrix({ data }: Props) {
  return (
    <section
      className="rounded-2xl border bg-white p-6 shadow-sm"
      data-testid="search-impact-coverage-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold" data-testid="search-impact-coverage-title">
            Descriptor coverage
          </h2>
          <p className="text-muted-foreground mt-1 max-w-[92ch] text-sm leading-relaxed">
            Every search descriptor used to write this product, and which fields it actually
            reached. A descriptor that lands nowhere is demand the content isn&apos;t competing for.
          </p>
        </div>
        <div className="text-muted-foreground flex flex-wrap gap-4 pt-1 text-xs font-semibold">
          {data.confidence !== null && (
            <span>
              Confidence{' '}
              <b className="text-foreground" data-testid="search-impact-coverage-confidence-value">
                {Math.round(data.confidence * 100)}%
              </b>
            </span>
          )}
          {data.generatedAt && (
            <span>
              Generated{' '}
              <b className="text-foreground" data-testid="search-impact-coverage-generated-value">
                {formatDateTime(data.generatedAt)}
              </b>
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-205 border-collapse text-sm"
            data-testid="search-impact-coverage-table"
          >
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs font-semibold">
                <th scope="col" className="min-w-62 px-4 py-3 text-left">
                  Search descriptor
                </th>
                {data.fields.map((field) => (
                  <th
                    key={field.key}
                    scope="col"
                    className="px-2.5 py-3 text-center whitespace-nowrap"
                  >
                    {field.label}
                  </th>
                ))}
                <th scope="col" className="px-4 py-3 text-right">
                  Coverage
                </th>
              </tr>
            </thead>
            <tbody>
              {data.descriptors.map((descriptor) => {
                const coverage = descriptorCoverage(descriptor);
                const withheld = descriptor.withheldReason !== null;
                return (
                  <tr
                    key={descriptor.term}
                    data-testid={`search-impact-coverage-row-${descriptor.term}`}
                    className={cn('border-t', withheld ? 'bg-warning-50' : 'hover:bg-muted/30')}
                    title={descriptor.withheldReason ?? undefined}
                  >
                    <td className="px-4 py-3.5">
                      <div className="font-semibold">{descriptor.term}</div>
                      <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2.5 text-xs font-semibold">
                        <span
                          data-testid={`search-impact-coverage-row-${descriptor.term}-volume-value`}
                        >
                          {descriptor.monthlyVolume === null
                            ? '—'
                            : `${formatVolume(descriptor.monthlyVolume)}/mo`}
                        </span>
                        {/* Null until Keyword Planner has the term (or Google Ads isn't linked). */}
                        {descriptor.competition !== null && (
                          <span
                            className="text-foreground inline-flex items-center gap-1.5 font-semibold"
                            data-testid={`search-impact-coverage-row-${descriptor.term}-competition-badge`}
                          >
                            <span
                              className={cn(
                                'h-1.5 w-1.5 rounded-full',
                                COMPETITION_BEAD[descriptor.competition],
                              )}
                              aria-hidden="true"
                            />
                            {descriptor.competition}
                            {descriptor.competitionIndex !== null &&
                              ` (${String(descriptor.competitionIndex)})`}
                          </span>
                        )}
                        {withheld && (
                          <span
                            className="text-warning font-semibold"
                            data-testid={`search-impact-coverage-row-${descriptor.term}-withheld-badge`}
                          >
                            Withheld
                          </span>
                        )}
                      </div>
                    </td>
                    {data.fields.map((field) => (
                      <td
                        key={field.key}
                        className="px-2.5 py-3.5 text-center"
                        data-testid={`search-impact-coverage-cell-${descriptor.term}-${field.key}`}
                      >
                        <PlacementCell placement={descriptor.placement[field.key]} />
                      </td>
                    ))}
                    <td
                      className="px-4 py-3.5 text-right font-semibold tabular-nums"
                      data-testid={`search-impact-coverage-row-${descriptor.term}-coverage-value`}
                    >
                      {coverage.hit}/{coverage.total}
                      <small className="text-muted-foreground block text-[11px] font-semibold">
                        fields
                      </small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div
        className="text-muted-foreground flex flex-wrap gap-5 px-0.5 pt-3.5 text-xs font-semibold"
        data-testid="search-impact-coverage-legend"
      >
        <span className="inline-flex items-center gap-2">
          <PlacementCell placement="placed" /> Placed
        </span>
        <span className="inline-flex items-center gap-2">
          <PlacementCell placement="not-placed" /> Not placed
        </span>
        <span className="inline-flex items-center gap-2">
          <PlacementCell placement="not-applicable" /> Field not applicable to this descriptor
        </span>
      </div>
    </section>
  );
}
